import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { Page } from "../../../components/page-base";
import useUserStore from "../../../store/user";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import { adminOverviewService } from "../services";
import type { Fatia, VisaoGeral } from "../types";
import {
  CORES_DE_ACESSO,
  CORES_DE_STATUS,
  GOOGLE,
  ROTULO_DE_ACESSO,
  ROTULO_DE_STATUS,
  mesCurto,
  percentual,
  rotulo,
  topComOutros,
  total,
  ultimosDozeMeses,
} from "./graficos";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);
ChartJS.defaults.font.family = "var(--font-sans), system-ui, sans-serif";
ChartJS.defaults.color = "#5F6368";

/* Prancheta PainelControle: main com 28px 32px, blocos em cartões brancos de 16px. */
const Conteudo = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  min-width: 0;
  padding: 28px 32px 40px;
  background: var(--bg);
  font-family: var(--font-sans);
  color: var(--ink);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;

  h1 {
    font-family: var(--font-sans);
    font-size: 32px;
    line-height: 40px;
    font-weight: 400;
    letter-spacing: 0;
    margin: 0;
    color: #202124;
  }
  h2 {
    font-family: var(--font-sans);
    font-size: 20px;
    font-weight: 500;
    margin: 0;
  }
  @media (max-width: 899px) {
    padding: 20px 16px 48px;
  }
  @media (max-width: 599.95px) {
    h1 {
      font-size: 26px;
      line-height: 32px;
    }
    h2 {
      font-size: 18px;
    }
  }
`;

const Apoio = styled.span`
  font-size: 13px;
  color: var(--text-muted);
`;

const Subtitulo = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-muted);
`;

const Cartao = styled.section`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-sizing: border-box;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  @media (max-width: 599.95px) {
    padding: 18px 16px;
  }
`;

const Kpis = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;
  }
`;

const Kpi = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.25s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(60, 64, 67, 0.1);
  }
  .topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .rotulo {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
  }
  .numero {
    font-family: var(--font-sans);
    font-size: 44px;
    line-height: 48px;
    font-weight: 500;
    color: #1f1f1f;
    letter-spacing: -1px;
  }
  .apoio {
    font-size: 13px;
    color: #3c4043;
  }
  .topo .material-icons {
    flex-shrink: 0;
  }
  @media (max-width: 599.95px) {
    padding: 16px;
    gap: 10px;
    .rotulo {
      font-size: 13px;
    }
    .numero {
      font-size: 30px;
      line-height: 36px;
    }
    .apoio {
      font-size: 12px;
    }
  }
`;

const Chip = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? "#202124" : "#dadce0")};
  background: ${({ $on }) => ($on ? "#202124" : "#ffffff")};
  color: ${({ $on }) => ($on ? "#ffffff" : "#3c4043")};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ $on }) => ($on ? "#202124" : "#f1f3f4")};
  }
`;

const Botao = styled.button<{ $variante?: "tonal" | "outlined" | "plain"; $pequeno?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: ${({ $pequeno }) => ($pequeno ? "36px" : "44px")};
  padding: ${({ $pequeno }) => ($pequeno ? "0 12px" : "0 20px")};
  border-radius: ${({ $pequeno }) => ($pequeno ? "10px" : "12px")};
  font-family: var(--font-sans);
  font-size: ${({ $pequeno }) => ($pequeno ? "13px" : "14px")};
  font-weight: 500;
  border: 1px solid ${({ $variante }) => ($variante === "outlined" ? "#dadce0" : "transparent")};
  background: ${({ $variante }) => ($variante === "tonal" ? "#EAF1FB" : "#ffffff")};
  color: var(--brand-700);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  &:hover {
    background: ${({ $variante }) => ($variante === "tonal" ? "#D6E4F7" : "#F8FAFD")};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .material-icons {
    font-size: 18px;
  }
`;

const Badge = styled.span<{ $fundo?: string; $cor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $fundo }) => $fundo ?? "#F1F3F4"};
  color: ${({ $cor }) => $cor ?? "#5F6368"};
  .material-icons {
    font-size: 16px;
  }
`;

const Grade = styled.div<{ $colunas?: string }>`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
  @media (min-width: 1100px) {
    grid-template-columns: ${({ $colunas }) => $colunas ?? "repeat(2, minmax(0, 1fr))"};
  }
`;

const LinhaDeBarra = styled.div<{ $rotulo: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  .rotulo {
    width: ${({ $rotulo }) => $rotulo}px;
    text-align: right;
    font-size: 13px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
  }
  .trilho {
    flex: 1;
    height: 22px;
    border-radius: 6px;
    background: #f8f9fa;
    overflow: hidden;
    transition: background 0.15s;
  }
  &:hover .trilho {
    background: #f1f3f4;
  }
  .trilho > span {
    display: block;
    height: 100%;
    border-radius: 6px;
    transform-origin: left;
    animation: crescer 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s both;
  }
  .valor {
    width: 56px;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: #202124;
    flex-shrink: 0;
  }
  /* Celular: o rótulo vai para cima da barra, senão a barra some atrás dele. */
  @media (max-width: 599.95px) {
    flex-wrap: wrap;
    row-gap: 4px;
    .rotulo {
      width: 100%;
      text-align: left;
      white-space: normal;
    }
  }
  @keyframes crescer {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .trilho > span {
      animation: none;
    }
  }
`;

const Legenda = styled.div`
  /* Abaixo de ~240 px ao lado da rosca, a legenda desce para baixo dela (celular). */
  flex: 1 1 240px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  .item {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .ponto {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex-shrink: 0;
  }
  .nome {
    flex: 1 1 auto;
    font-size: 14px;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .valor {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
`;

const Cidade = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f4;
  &:last-child {
    border-bottom: 0;
  }
  .posicao {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #f1f3f4;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .nome {
    flex: 1;
    font-size: 14px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .uf {
    font-size: 12px;
    color: var(--text-muted);
  }
  .valor {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
    width: 44px;
    text-align: right;
  }
`;

const linha: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (contexto) => ` ${contexto.parsed.y} perfis` } } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: { beginAtZero: true, grid: { color: "#E2E6EA" }, ticks: { precision: 0, font: { size: 11 } } },
  },
  elements: { line: { tension: 0.35, borderWidth: 2.5, borderColor: GOOGLE.azul, fill: true, backgroundColor: "rgba(18,100,199,0.10)" }, point: { radius: 3, backgroundColor: GOOGLE.azul } },
};

/** Baixa um CSV simples (rótulo;valor) com o que está no bloco. */
const exportarCsv = (nome: string, fatias: Fatia[]) => {
  const linhas = ["rotulo;perfis", ...fatias.map((fatia) => `"${fatia.label.replaceAll('"', '""')}";${fatia.value}`)];
  const blob = new Blob([String.fromCharCode(0xfeff) + linhas.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${nome}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const Barras = ({ titulo, apoio, fatias, cor, larguraDoRotulo, arquivo }: { titulo: string; apoio: string; fatias: Fatia[]; cor: string; larguraDoRotulo: number; arquivo: string }) => {
  const maior = Math.max(0, ...fatias.map((fatia) => fatia.value));
  return (
    <Cartao>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2>{titulo}</h2>
          <Apoio>{apoio}</Apoio>
        </div>
        <Botao type="button" $variante="outlined" $pequeno onClick={() => exportarCsv(arquivo, fatias)} disabled={fatias.length === 0}>
          <span className="material-icons">download</span>
          Exportar
        </Botao>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {fatias.map((fatia) => (
          <LinhaDeBarra key={fatia.label} $rotulo={larguraDoRotulo} title={`${fatia.label}: ${fatia.value.toLocaleString("pt-BR")} perfis`}>
            <span className="rotulo">{fatia.label}</span>
            <div className="trilho">
              <span style={{ width: `${maior ? (fatia.value / maior) * 100 : 0}%`, background: cor }} />
            </div>
            <span className="valor">{fatia.value.toLocaleString("pt-BR")}</span>
          </LinhaDeBarra>
        ))}
        {fatias.length === 0 && <Apoio>Sem dados para este filtro.</Apoio>}
      </div>
    </Cartao>
  );
};

/** Rosca em conic-gradient com o total no centro e legenda com percentuais (prancheta PainelControle). */
const Rosca = ({ titulo, apoio, fatias, cores: paleta, rotulos, rodape }: { titulo: string; apoio: string; fatias: Fatia[]; cores: Record<string, string>; rotulos: Record<string, string>; rodape?: React.ReactNode }) => {
  const visiveis = fatias.filter((fatia) => fatia.value > 0);
  const soma = total(visiveis);
  let acumulado = 0;
  const trechos = visiveis.map((fatia) => {
    const inicio = acumulado;
    acumulado += soma ? (fatia.value / soma) * 100 : 0;
    return `${paleta[fatia.label] ?? GOOGLE.cinza} ${inicio}% ${acumulado}%`;
  });
  const fundo = trechos.length ? `conic-gradient(${trechos.join(", ")})` : "#F1F3F4";
  return (
    <Cartao>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2>{titulo}</h2>
        <Apoio>{apoio}</Apoio>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "24px 36px", flexWrap: "wrap" }}>
        <div role="img" aria-label={`${titulo}: ${soma.toLocaleString("pt-BR")} no total`} style={{ width: 180, height: 180, borderRadius: 999, background: fundo, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <div style={{ width: 124, height: 124, borderRadius: 999, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "var(--font-sans)", fontSize: 28, fontWeight: 500, letterSpacing: "-0.5px" }}>{soma.toLocaleString("pt-BR")}</span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>total</span>
          </div>
        </div>
        <Legenda>
          {visiveis.map((fatia) => {
            const cor = paleta[fatia.label] ?? GOOGLE.cinza;
            return (
              <div className="item" key={fatia.label}>
                <span className="ponto" style={{ background: cor }} />
                <span className="nome">{rotulo(rotulos, fatia.label)}</span>
                <span className="valor">{fatia.value.toLocaleString("pt-BR")}</span>
                <Badge $fundo={`${cor}1F`} $cor={cor}>{`${percentual(fatia.value, soma)}%`}</Badge>
              </div>
            );
          })}
          {visiveis.length === 0 && <Apoio>Sem dados.</Apoio>}
          {rodape}
        </Legenda>
      </div>
    </Cartao>
  );
};

export const ProfileDashboard = () => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const token = user?.token ?? "";
  const [uf, setUf] = useState<string | null>(null);
  const [dados, setDados] = useState<VisaoGeral | null>(null);
  const [ufsDisponiveis, setUfsDisponiveis] = useState<Fatia[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (!token) return;
    let ativo = true;
    setCarregando(true);
    adminOverviewService.getVisaoGeral(token, uf).then((resposta) => {
      if (!ativo) return;
      setDados(resposta);
      if (resposta && !uf) setUfsDisponiveis(resposta.perfisPorUf);
      setCarregando(false);
    });
    return () => {
      ativo = false;
    };
  }, [token, uf, versao]);

  const atualizar = useCallback(() => setVersao((atual) => atual + 1), []);

  const ufs = useMemo(() => ufsDisponiveis.filter((fatia) => fatia.label !== "Sem UF").slice(0, 10), [ufsDisponiveis]);
  const porUf = useMemo(() => topComOutros(dados?.perfisPorUf ?? [], 10), [dados]);
  const porCategoria = useMemo(() => topComOutros(dados?.perfisPorCategoria ?? [], 10), [dados]);
  const porMes = useMemo(() => ultimosDozeMeses(dados?.novosPerfisPorMes ?? []), [dados]);
  const temHistorico = useMemo(() => porMes.some((fatia) => fatia.value > 0), [porMes]);
  const cidades = useMemo(() => (dados?.perfisPorCidade ?? []).slice(0, 8), [dados]);
  const geradoEm = dados?.generatedAt ? new Date(dados.generatedAt) : null;
  const irParaContas = () => navigate("/profile-quality");

  return (
    <Page.Root>
      <Conteudo id="dashboard-container">
        <Page.HeaderAux />
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <h1>Visão geral da base</h1>
            <Subtitulo>Contagens reais do banco: perfis de clientes ativos, por região, categoria e situação de acesso.</Subtitulo>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Badge>
              <span className="material-icons">schedule</span>
              {geradoEm ? `Atualizado em ${geradoEm.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}` : "Ainda não atualizado"}
            </Badge>
            <Botao type="button" $variante="tonal" onClick={atualizar} disabled={carregando}>
              <span className="material-icons">refresh</span>
              {carregando ? "Atualizando…" : "Atualizar"}
            </Botao>
          </div>
        </div>

        <div role="tablist" aria-label="Filtrar por UF" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Chip type="button" role="tab" aria-selected={uf === null} $on={uf === null} onClick={() => setUf(null)}>
            Brasil
          </Chip>
          {ufs.map((fatia) => (
            <Chip key={fatia.label} type="button" role="tab" aria-selected={uf === fatia.label} $on={uf === fatia.label} onClick={() => setUf(fatia.label)}>
              {fatia.label}
            </Chip>
          ))}
        </div>

        <Kpis>
          <Kpi>
            <div className="topo">
              <span className="rotulo" style={{ color: "#0B4FA8" }}>{uf ? `Perfis ativos em ${uf}` : "Perfis ativos"}</span>
              <span className="material-icons" style={{ color: "#0B4FA8" }}>storefront</span>
            </div>
            <div className="numero">{dados ? <NumeroAnimado valor={dados.perfisAtivos} /> : "—"}</div>
            <div className="apoio">{dados ? `de ${dados.clientesTotal.toLocaleString("pt-BR")} clientes cadastrados` : "carregando"}</div>
          </Kpi>
          <Kpi>
            <div className="topo">
              <span className="rotulo" style={{ color: "#137333" }}>Em acompanhamento</span>
              <span className="material-icons" style={{ color: "#137333" }}>visibility</span>
            </div>
            <div className="numero">{dados ? <NumeroAnimado valor={dados.perfisEmAcompanhamento} /> : "—"}</div>
            <div className="apoio">{dados ? `${percentual(dados.perfisEmAcompanhamento, dados.perfisAtivos)}% dos perfis ativos` : ""}</div>
          </Kpi>
          <Kpi>
            <div className="topo">
              <span className="rotulo" style={{ color: "#B06000" }}>Contas gestoras conectadas</span>
              <span className="material-icons" style={{ color: "#B06000" }}>manage_accounts</span>
            </div>
            <div className="numero">{dados ? <NumeroAnimado valor={dados.contasConectadas} /> : "—"}</div>
            <div className="apoio">{dados ? `de ${dados.contasGestoras} contas · ${dados.convitesPendentes} convites pendentes` : ""}</div>
          </Kpi>
          <Kpi>
            <div className="topo">
              <span className="rotulo" style={{ color: "#C5221F" }}>Precisam reconectar</span>
              <span className="material-icons" style={{ color: "#C5221F" }}>sync_problem</span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
              <div className="numero">{dados ? <NumeroAnimado valor={dados.contasPrecisamReconectar} /> : "—"}</div>
              <Botao type="button" $variante="plain" $pequeno style={{ color: "#C5221F" }} onClick={irParaContas}>
                Ver contas
                <span className="material-icons">arrow_forward</span>
              </Botao>
            </div>
            <div className="apoio">contas com token vencido ou revogado</div>
          </Kpi>
        </Kpis>

        {carregando && !dados ? (
          <Apoio>Carregando o panorama…</Apoio>
        ) : dados ? (
          <>
            <Grade>
              <Barras titulo="Perfis por estado" apoio="Perfis de clientes ativos, pelo endereço da ficha." fatias={porUf} cor={GOOGLE.azul} larguraDoRotulo={52} arquivo="perfis-por-estado" />
              <Barras titulo={uf ? `Categorias mais comuns em ${uf}` : "Categorias mais comuns"} apoio="Categoria principal cadastrada no Google." fatias={porCategoria} cor="#1E8E3E" larguraDoRotulo={220} arquivo="categorias-mais-comuns" />
            </Grade>

            <Grade>
              <Rosca titulo="Clientes por status" apoio="Todos os clientes cadastrados, pelo status da conta." fatias={dados.clientesPorStatus} cores={CORES_DE_STATUS} rotulos={ROTULO_DE_STATUS} />
              <Rosca
                titulo={uf ? `Situação de acesso em ${uf}` : "Situação de acesso dos perfis"}
                apoio="Última verificação com o Google: acesso, suspensão e cópia."
                fatias={dados.perfisPorAcesso}
                cores={CORES_DE_ACESSO}
                rotulos={ROTULO_DE_ACESSO}
                rodape={
                  <Botao type="button" $variante="tonal" $pequeno style={{ alignSelf: "flex-start", marginTop: 6 }} onClick={irParaContas}>
                    Ver contas com problema
                    <span className="material-icons">arrow_forward</span>
                  </Botao>
                }
              />
            </Grade>

            <Grade $colunas="minmax(0, 1.4fr) minmax(0, 1fr)">
              <Cartao>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <h2>Novos perfis por mês</h2>
                    <Apoio>Perfis de clientes ativos cadastrados nos últimos 12 meses. Fonte: banco GeoRanking.</Apoio>
                  </div>
                  <Badge>12 meses</Badge>
                </div>
                {temHistorico ? (
                  <div style={{ position: "relative", height: 240 }}>
                    <Line data={{ labels: porMes.map((f) => mesCurto(f.label)), datasets: [{ data: porMes.map((f) => f.value) }] }} options={linha} />
                  </div>
                ) : (
                  <div style={{ height: 160, borderRadius: 12, background: "var(--bg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, textAlign: "center" }}>
                    <span className="material-icons" style={{ fontSize: 30, color: "#9AA0A6" }}>bar_chart</span>
                    <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 500 }}>Dados históricos ainda não disponíveis</span>
                    <Apoio style={{ fontSize: 12 }}>A série mensal aparece quando o backend registrar a data de cadastro dos perfis.</Apoio>
                  </div>
                )}
              </Cartao>

              <Cartao style={{ gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <h2>{uf ? `Maiores cidades em ${uf}` : "Maiores cidades"}</h2>
                  <Apoio>As oito maiores concentrações da base. Fonte: banco GeoRanking.</Apoio>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {cidades.map((cidade, indice) => {
                    const [nome, estado] = cidade.label.split(" / ");
                    return (
                      <Cidade key={cidade.label}>
                        <span className="posicao">{indice + 1}</span>
                        <span className="nome">{nome}</span>
                        {estado && <span className="uf">{estado}</span>}
                        <span className="valor">{cidade.value.toLocaleString("pt-BR")}</span>
                      </Cidade>
                    );
                  })}
                  {cidades.length === 0 && <Apoio>Sem cidades cadastradas para este filtro.</Apoio>}
                </div>
              </Cartao>
            </Grade>
          </>
        ) : (
          <Apoio>Não foi possível carregar o panorama.</Apoio>
        )}

        <Apoio>
          “Perfil ativo” é o perfil de um cliente com status ativo no GeoRanking; o Google não informa se a ficha está ativa — o que ele informa (acesso, suspensão, cópia) está em “situação de acesso”.
        </Apoio>
      </Conteudo>
    </Page.Root>
  );
};
